import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Bell, Wrench } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

export const WO_STATUS_LABELS = { pending: "Pendiente", in_progress: "En proceso", paused: "En pausa", completed: "Terminado", cancelled: "Cancelado" };

const vehicleText = (v) => [v?.brand, v?.model, v?.year, v?.plates].filter(Boolean).join(" ");

function Notifications({ items, onRead }) {
  if (!items.length) return <p className="text-sm text-muted-foreground" data-testid="notifications-empty">Sin notificaciones.</p>;
  return (
    <div className="space-y-2">
      {items.map((n) => (
        <div key={n.id} className={`flex items-center justify-between gap-3 rounded-md border border-border p-3 ${n.status === "open" ? "bg-amber-50" : ""}`} data-testid={`notification-${n.id}`}>
          <div className="min-w-0">
            <div className="text-sm font-medium">{n.title}</div>
            <div className="text-xs text-muted-foreground truncate">{n.message} · {(n.generated_at || "").slice(0, 16).replace("T", " ")}</div>
          </div>
          <div className="flex gap-2 shrink-0">
            <Button size="sm" variant="outline" asChild onClick={() => n.status === "open" && onRead(n.id)}>
              <Link to={n.metadata?.link || "/mis-trabajos"} data-testid={`notification-open-${n.id}`}>Abrir</Link>
            </Button>
          </div>
        </div>
      ))}
    </div>
  );
}

export default function MisTrabajos() {
  const [wos, setWos] = useState([]);
  const [notifs, setNotifs] = useState([]);

  const load = useCallback(async () => {
    try {
      const [{ data: w }, { data: n }] = await Promise.all([api.get("/my/work-orders"), api.get("/my/notifications")]);
      setWos(w || []);
      setNotifs(n || []);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const markRead = (id) => api.post(`/my/notifications/${id}/read`).then(load).catch(() => {});

  return (
    <div className="space-y-8" data-testid="page-mis-trabajos">
      <PageHeader title="Mis trabajos" description="Órdenes de trabajo asignadas a ti en la sucursal activa." />
      <section className="space-y-3">
        <h2 className="text-sm font-semibold flex items-center gap-2"><Bell className="h-4 w-4" /> Notificaciones</h2>
        <Notifications items={notifs} onRead={markRead} />
      </section>
      <section className="space-y-3">
        <h2 className="text-sm font-semibold flex items-center gap-2"><Wrench className="h-4 w-4" /> Mis órdenes de trabajo</h2>
        {wos.length === 0 && <p className="text-sm text-muted-foreground" data-testid="my-wos-empty">No tienes trabajos asignados.</p>}
        <div className="grid gap-3 md:grid-cols-2">
          {wos.map((w) => (
            <Card key={w.id} className="p-4 border-border space-y-1" data-testid={`my-wo-${w.id}`}>
              <div className="flex items-center justify-between gap-2">
                <span className="mono text-sm">{w.service_order.folio}</span>
                <Badge variant="secondary">{WO_STATUS_LABELS[w.work_status] || w.work_status}</Badge>
              </div>
              <div className="font-medium">{w.title}</div>
              <div className="text-xs text-muted-foreground">{vehicleText(w.service_order.vehicle)}</div>
              {w.service_order.stage_name && <div className="text-xs">{w.service_order.service_flow_name} · {w.service_order.stage_name}</div>}
              <Button size="sm" variant="outline" asChild className="mt-2">
                <Link to={`/mis-trabajos/${w.service_order.id}`} data-testid={`my-wo-open-${w.id}`}>Ver trabajo</Link>
              </Button>
            </Card>
          ))}
        </div>
      </section>
    </div>
  );
}
